import { useEffect, useMemo, useState } from "react";
import {
  Inbox,
  KeyRound,
  Loader2,
  PanelLeft,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { toast } from "sonner";
import useAdmin from "../../Sevices/UseAdmin";

import RolesPermissionsTab from "./RolesPermissionsTab";
import CreateRoleTab from "./CreateRoleTab";
import AssignRoleTab from "./AssignRoleTab";
import SidebarAccessTab from "./SidebarAccessTab";

const getRoleId = (role) => {
  return String(role?.pk_role_id || role?.role_id || role?.id || "");
};

const getRoleName = (role) => {
  const rawRole =
    role?.role ||
    role?.roleName ||
    role?.role_name ||
    "Unnamed Role";

  return String(rawRole).split("|")[0]?.trim() || "Unnamed Role";
};

const getSidebarAccessId = (item) => {
  return String(item?.id || item?.pk_sidebar_id || "");
};

const flattenSidebarAccess = (accessList = []) => {
  const result = [];

  accessList.forEach((menu) => {
    const submenus = Array.isArray(menu?.submenus) ? menu.submenus : [];

    if (submenus.length === 0) {
      result.push({
        id: getSidebarAccessId(menu),
        pk_menu_id: menu?.pk_menu_id,
        pk_submenu_id: menu?.pk_submenu_id,
        menu_title: menu?.menu_title,
        menu_icon: menu?.menu_icon,
        menu_url: menu?.menu_url,
        menu_type: menu?.menu_type,
        status: menu?.status,
        submenu_title: menu?.menu_title,
        submenu_icon: menu?.menu_icon,
        submenu_url: menu?.menu_url,
      });
      return;
    }

    submenus.forEach((submenu) => {
      result.push({
        id: submenu?.id || menu?.id,
        pk_menu_id: menu?.pk_menu_id,
        pk_submenu_id: submenu?.id || menu?.pk_submenu_id,
        menu_title: menu?.menu_title,
        menu_icon: menu?.menu_icon,
        menu_url: menu?.menu_url,
        menu_type: menu?.menu_type,
        status: submenu?.status || menu?.status,
        submenu_title: submenu?.submenu_title || menu?.menu_title,
        submenu_icon: submenu?.submenu_icon || menu?.menu_icon,
        submenu_url: submenu?.submenu_url || menu?.menu_url,
      });
    });
  });

  return result;
};

const InlineLoader = ({ text = "Loading...", className = "" }) => {
  return (
    <div
      className={`flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500 ${className}`}
    >
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{text}</span>
    </div>
  );
};

const EmptyState = ({
  title = "No data found",
  description = "Nothing to show here.",
}) => {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
      <div className="mb-3 rounded-full bg-white p-3 shadow-sm">
        <Inbox className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <p className="mt-1 max-w-sm text-xs text-slate-500">{description}</p>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, helper }) => {
  return (
    <div className="shrink-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500">{title}</p>
          <h3 className="mt-1 text-xl font-bold text-slate-900">{value}</h3>
          {helper ? (
            <p className="mt-1 text-xs text-slate-400">{helper}</p>
          ) : null}
        </div>

        <div className="rounded-xl bg-orange-50 p-2.5 text-orange-600">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

const AdminTopCards = ({
  permissions = [],
  roles = [],
  admins = [],
  sidebarAccess = [],
}) => {
  return (
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
      <StatCard
        title="Total Roles"
        value={roles.length}
        helper="Roles available"
        icon={ShieldCheck}
      />
      <StatCard
        title="Permission Keys"
        value={permissions.length}
        helper="Controls received"
        icon={KeyRound}
      />
      <StatCard
        title="Admins"
        value={admins.length}
        helper="Admin users"
        icon={UsersRound}
      />
      <StatCard
        title="Sidebar Items"
        value={flattenSidebarAccess(sidebarAccess).length}
        helper="Menus + submenus"
        icon={PanelLeft}
      />
    </div>
  );
};

const AdminTabs = ({ activeTab, onChange }) => {
  const tabs = [
    { id: "roles", label: "Roles & Permissions" },
    { id: "create", label: "Create Role" },
    { id: "assign", label: "Assign Role" },
    { id: "sidebar", label: "Sidebar Access" },
  ];

  return (
    <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
              isActive
                ? "bg-orange-500 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

const Admin = () => {
  const [activeTab, setActiveTab] = useState("roles");

  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [admins, setAdmins] = useState([]);

  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [rolePermissions, setRolePermissions] = useState([]);
  const [permissionSearchText, setPermissionSearchText] = useState("");

  const [allSidebarAccess, setAllSidebarAccess] = useState([]);
  const [selectedUserAccess, setSelectedUserAccess] = useState([]);
  const [selectedAccessAdminId, setSelectedAccessAdminId] = useState("");

  const {
    FetchPermissionKeys,
    FetchAllRoles,
    InsertRole,
    FetchRolePermissions,
    UpdateRolePermissionStatus,
    UpdateRoleStatus,
    AssignRoleToAdmin,
    FetchAllAdmins,
    CreateAdmin,

    FetchUserAccess,
    UpdateSideBarStatus,

    permissionsLoading,
    rolesLoading,
    rolePermissionsLoading,
    createRoleLoading,
    updateLoading,
    assignRoleLoading,
    adminsLoading,
    createAdminLoading,

    userAccessLoading,
    sideBarUpdateLoading,
  } = useAdmin();

  const selectedRole = useMemo(() => {
    return roles.find(
      (role) => String(getRoleId(role)) === String(selectedRoleId),
    );
  }, [roles, selectedRoleId]);

  const loadAllSidebarAccess = async () => {
    if (typeof FetchUserAccess !== "function") return [];
    return await FetchUserAccess("", setAllSidebarAccess);
  };

  const loadSelectedUserAccess = async (adminId) => {
    if (!adminId) {
      setSelectedUserAccess([]);
      return [];
    }

    if (typeof FetchUserAccess !== "function") return [];

    return await FetchUserAccess(adminId, setSelectedUserAccess);
  };

  const refreshAdmins = async () => {
    await FetchAllAdmins("", setAdmins);
  };

  const loadBaseData = async () => {
    try {
      const [permissionData, roleData, adminData, sidebarData] =
        await Promise.all([
          FetchPermissionKeys("", setPermissions),
          FetchAllRoles("", setRoles),
          FetchAllAdmins("", setAdmins),
          loadAllSidebarAccess(),
        ]);

      const safeRoleData = Array.isArray(roleData) ? roleData : [];

      const firstRoleId = safeRoleData.length ? getRoleId(safeRoleData[0]) : "";

      if (!selectedRoleId && firstRoleId) {
        setSelectedRoleId(firstRoleId);

        await FetchRolePermissions(firstRoleId, setRolePermissions);
      }

      if (!Array.isArray(permissionData) || permissionData.length === 0) {
        setRolePermissions([]);
      }
    } catch (error) {
      console.error("LOAD BASE DATA ERROR:", error);
      toast.error(error?.message || "Unable to load admin data");
    }
  };

  useEffect(() => {
    loadBaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleWindowError = (event) => {
      console.group("ADMIN PAGE GLOBAL ERROR");

      console.error("Message:", event.message);
      console.error("File:", event.filename);
      console.error("Line:", event.lineno);
      console.error("Column:", event.colno);
      console.error("Error object:", event.error);
      console.error("Stack:", event.error?.stack);

      console.groupEnd();
    };

    const handleUnhandledRejection = (event) => {
      console.group("ADMIN PAGE PROMISE ERROR");

      console.error("Reason:", event.reason);
      console.error("Message:", event.reason?.message);
      console.error("Stack:", event.reason?.stack);

      console.groupEnd();
    };

    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);

  const refreshRoles = async () => {
    const data = await FetchAllRoles("", setRoles);

    if (selectedRoleId) {
      const stillAvailable = data.some(
        (role) => String(getRoleId(role)) === String(selectedRoleId),
      );

      if (!stillAvailable) {
        setSelectedRoleId("");
        setRolePermissions([]);
      }
    }
  };

  const handleSelectRole = async (roleId) => {
    setSelectedRoleId(roleId);
    setPermissionSearchText("");

    if (!roleId) {
      setRolePermissions([]);
      return;
    }

    await FetchRolePermissions(roleId, setRolePermissions);
  };

  const handleCreateRole = async ({
    roleName,
    accessManagement,
    permissionIds,
  }) => {
    if (!roleName?.trim()) {
      toast.error("Role name is required");
      return false;
    }

    if (!Array.isArray(accessManagement) || accessManagement.length === 0) {
      toast.error("Please select at least one sidebar option");
      return false;
    }

    if (!Array.isArray(permissionIds) || permissionIds.length === 0) {
      toast.error("Please select at least one permission");
      return false;
    }

    return await InsertRole(
      {
        roleName,
        accessManagement,
        permissionIds,
      },
      async () => {
        const updatedRoles = await FetchAllRoles("", setRoles);

        const createdRole = updatedRoles.find(
          (role) =>
            getRoleName(role).toLowerCase() === roleName.trim().toLowerCase(),
        );

        if (createdRole) {
          const newRoleId = getRoleId(createdRole);
          setSelectedRoleId(newRoleId);
          setActiveTab("roles");
          await FetchRolePermissions(newRoleId, setRolePermissions);
        }
      },
    );
  };

const handleUpdateRoleStatus = async (roleId, status) => {
  await UpdateRoleStatus(roleId, status, async () => {
    await FetchAllRoles("", setRoles);

    if (String(selectedRoleId) === String(roleId)) {
      await FetchRolePermissions(
        roleId,
        setRolePermissions,
      );
    }
  });
};

const handleToggleRolePermission = async (
  permission,
  rolePermissionRow,
  checked,
) => {
  if (!selectedRoleId) {
    toast.error("Please select a role first");
    return;
  }

  if (!rolePermissionRow?.pk_permission_roles_id) {
    toast.error("Permission mapping ID is missing for this role");
    return;
  }

  await UpdateRolePermissionStatus(
    rolePermissionRow.pk_permission_roles_id,
    checked ? "Y" : "N",
    async () => {
      await FetchRolePermissions(
        selectedRoleId,
        setRolePermissions,
      );
    },
  );
};

  const handleCreateAdmin = async (adminPayload) => {
    return await CreateAdmin(adminPayload, refreshAdmins);
  };

  const handleAssignRole = async (adminId, roleId) => {
    return await AssignRoleToAdmin(adminId, roleId, refreshAdmins);
  };

  const handleToggleSidebarStatus = async (id, status) => {
    if (typeof UpdateSideBarStatus !== "function") {
      toast.error("UpdateSideBarStatus function missing in service");
      return;
    }

    await UpdateSideBarStatus(id, status, async () => {
      await loadAllSidebarAccess();

      if (selectedAccessAdminId) {
        await loadSelectedUserAccess(selectedAccessAdminId);
      }
    });
  };

  const pageLoading = permissionsLoading || rolesLoading;

  return (
    <div className="absolute inset-0 min-h-0 min-w-0 overflow-hidden bg-slate-50">
      <div className="mx-auto flex h-full min-h-0 min-w-0 w-full max-w-none flex-col overflow-hidden px-3 py-3 sm:px-4">
        {/* Top section */}
        <div className="shrink-0 space-y-3">
          <div className="flex flex-col gap-3 rounded-3xl bg-gradient-to-r from-orange-500 to-orange-300 p-4 text-white shadow-sm md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white">
                Admin Controls
              </p>

              <h1 className="mt-1 text-xl font-black md:text-2xl">
                Admin & Access Control
              </h1>

              <p className="mt-1 max-w-2xl text-xs text-white/90 md:text-sm">
                Create roles, map permissions, update sidebar access and assign
                roles to admin users from one place.
              </p>
            </div>

            <button
              type="button"
              onClick={loadBaseData}
              disabled={permissionsLoading || rolesLoading || userAccessLoading}
              className="shrink-0 rounded-xl bg-white px-4 py-2 text-sm font-bold text-slate-900 shadow-sm transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Refresh Admin Data
            </button>
          </div>

          {activeTab !== "create" && (
            <AdminTopCards
              permissions={permissions}
              roles={roles}
              admins={admins}
              sidebarAccess={allSidebarAccess}
            />
          )}

          <AdminTabs activeTab={activeTab} onChange={setActiveTab} />
        </div>

        {/* Remaining viewport area */}
        <div className="mt-3 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {pageLoading ? (
            <div className="h-full overflow-y-auto">
              <InlineLoader text="Loading admin module..." />
            </div>
          ) : activeTab === "roles" ? (
            <RolesPermissionsTab
              roles={roles}
              selectedRoleId={selectedRoleId}
              rolesLoading={rolesLoading}
              updateLoading={updateLoading}
              onRefresh={refreshRoles}
              onSelectRole={handleSelectRole}
              onUpdateRoleStatus={handleUpdateRoleStatus}
              selectedRole={selectedRole}
              permissions={permissions}
              rolePermissions={rolePermissions}
              searchText={permissionSearchText}
              onSearchChange={setPermissionSearchText}
              rolePermissionsLoading={rolePermissionsLoading}
              onTogglePermission={handleToggleRolePermission}
            />
          ) : activeTab === "create" ? (
            <CreateRoleTab
              permissions={permissions}
              allSidebarAccess={allSidebarAccess}
              permissionsLoading={permissionsLoading}
              userAccessLoading={userAccessLoading}
              createRoleLoading={createRoleLoading}
              onCreateRole={handleCreateRole}
            />
          ) : activeTab === "assign" ? (
            <AssignRoleTab
              roles={roles}
              admins={admins}
              adminsLoading={adminsLoading}
              createAdminLoading={createAdminLoading}
              assignRoleLoading={assignRoleLoading}
              onCreateAdmin={handleCreateAdmin}
              onAssignRole={handleAssignRole}
              onRefreshAdmins={refreshAdmins}
            />
          ) : (
            <SidebarAccessTab
              admins={admins}
              allSidebarAccess={allSidebarAccess}
              selectedUserAccess={selectedUserAccess}
              selectedAccessAdminId={selectedAccessAdminId}
              adminsLoading={adminsLoading}
              userAccessLoading={userAccessLoading}
              sideBarUpdateLoading={sideBarUpdateLoading}
              onSelectedAccessAdminChange={setSelectedAccessAdminId}
              onLoadAllAccess={loadAllSidebarAccess}
              onLoadUserAccess={loadSelectedUserAccess}
              onToggleSidebarStatus={handleToggleSidebarStatus}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
